import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import React from 'react';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Loading from 'src/modules/shared/components/atoms/loading';
import { WorkflowCanvas } from '../molecules/workflow-canvas/workflow-canvas';
import { WorkflowDetailsPanel } from '../molecules/workflow-details-panel';
import { WORKFLOW_START_NODE_ID } from '../molecules/workflow-canvas/workflow-canvas';
import { useUnsavedNavigationGuardState } from 'src/modules/shared/hooks/unsaved-navigation-guard';
import { UnsavedChangesModal } from 'src/modules/shared/components/molecules/modals/unsaved-changes-modal';
import { useActions } from 'src/modules/shared/hooks/api/automations/actions';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { WorkflowActionPickerModal } from 'src/modules/tools/components/molecules/workflow-editor/workflow-action-picker-modal';
import { WorkflowEditorHeaderActions } from 'src/modules/tools/components/molecules/workflow-editor/workflow-editor-header-actions';
import { WorkflowFormErrorBanner } from 'src/modules/tools/components/molecules/workflow-editor/workflow-form-error-banner';
import { useWorkflowCreate } from 'src/modules/tools/hooks/workflow-editor/use-workflow-create';
import { useWorkflowEditorDraft } from 'src/modules/tools/hooks/workflow-editor/use-workflow-editor-draft';
import { useWorkflowEditorUnsavedState } from 'src/modules/tools/hooks/workflow-editor/use-workflow-editor-unsaved';
import type { WorkflowCreatePageProps } from 'src/modules/tools/types/workflow-create.types';
import type { WorkflowEditorMetadataDraft } from 'src/modules/tools/types/workflow-editor.types';
import type { WorkflowDefinitionDraft } from 'src/modules/tools/utils/workflow-definition.type';

const WORKFLOW_END_NODE_ID = '__end__';
const DEFAULT_INPUT_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {},
};

/**
 * Create view for workflow versions. Renders the same canvas/details layout
 * as edit, starting from a blank definition.
 */
export const WorkflowCreatePage: React.FC<WorkflowCreatePageProps> = ({
  fromWorkflowId,
}) => {
  const [definitionDraft, setDefinitionDraft] =
    React.useState<WorkflowDefinitionDraft>(() => ({
      steps: {},
      inputSchema: DEFAULT_INPUT_SCHEMA,
    }));

  const [inputSchemaDraft, setInputSchemaDraft] = React.useState<
    Record<string, unknown>
  >(() => DEFAULT_INPUT_SCHEMA);

  const [selectedStepId, setSelectedStepId] = React.useState<
    string | undefined
  >();
  const [inputValidationErrors, setInputValidationErrors] = React.useState<
    string[]
  >([]);
  const { isBlocked, proceed, stay, setHasUnsavedChanges, hasUnsavedChanges } =
    useUnsavedNavigationGuardState(false);
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [actionSearch, setActionSearch] = React.useState('');
  const [metadataDraft, setMetadataDraft] =
    React.useState<WorkflowEditorMetadataDraft>({
      title: '',
      description: '',
    });
  const { actions: catalogActions, loading: loadingActions } = useActions(
    undefined,
    isAddModalOpen,
    true
  );
  const {
    pendingFinal,
    filteredActions,
    reachableSteps,
    hasSteps,
    hasInitialStep,
    handleRequestAddStep,
    handleRequestAddFinal,
    handleSelectAction,
    closeActionPicker,
    handleAddConnection,
    handleRemoveConnection,
    handleChangeStepInputs,
    handleDeleteStep,
  } = useWorkflowEditorDraft({
    definitionDraft,
    setDefinitionDraft,
    setSelectedStepId,
    actions: catalogActions,
    actionSearch,
    setActionSearch,
    setIsAddModalOpen,
    startNodeId: WORKFLOW_START_NODE_ID,
    endNodeId: WORKFLOW_END_NODE_ID,
  });
  const { resetBaseline, markSaved } = useWorkflowEditorUnsavedState({
    definitionDraft,
    inputSchemaDraft,
    metadataDraft,
    setHasUnsavedChanges,
  });

  React.useEffect(() => {
    const schema = definitionDraft.inputSchema;
    setInputSchemaDraft(
      schema ?? {
        type: 'object',
        properties: {},
      }
    );
  }, [definitionDraft]);

  const {
    formError,
    handleBack,
    handleSave,
    isSaving,
    shouldShowSourceLoading,
    shouldShowSourceError,
    sourceErrorMessage,
  } = useWorkflowCreate({
    fromWorkflowId,
    definitionDraft,
    inputSchemaDraft,
    metadataDraft,
    inputValidationErrors,
    hasUnsavedChanges,
    defaultInputSchema: DEFAULT_INPUT_SCHEMA,
    onSaveSuccess: markSaved,
    onHydrate: resetBaseline,
    setDefinitionDraft,
    setInputSchemaDraft,
    setMetadataDraft,
    setSelectedStepId,
  });
  const canSave =
    hasSteps &&
    hasInitialStep &&
    inputValidationErrors.length === 0 &&
    !isSaving;

  return (
    <div className="flex flex-col h-full">
      <UnsavedChangesModal
        isOpen={isBlocked}
        onProceed={proceed}
        onStay={stay}
        isSaving={isSaving}
      />
      <Panel className="h-full flex flex-col">
        <PanelHeader
          fixed
          className="px-4 py-3 border-b"
          actions={
            <WorkflowEditorHeaderActions
              onBack={handleBack}
              onSave={handleSave}
              canSave={canSave}
              isSaving={isSaving}
            />
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            <Text
              as="h2"
              size="h2"
              weight="medium"
              className="text-slate-900 break-words cursor-pointer"
              onClick={() => setSelectedStepId(undefined)}
            >
              {nonEmptyString(metadataDraft.title) ?? 'Nuevo flujo'}
            </Text>
            <Text size="small" className="text-slate-500">
              Creando flujo
            </Text>
          </div>
        </PanelHeader>
        <div className="flex-1 overflow-auto p-4">
          <div className="mb-3">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span>Nuevo flujo</span>
            </div>
            <WorkflowFormErrorBanner message={formError} />
          </div>
          {shouldShowSourceLoading ? (
            <div className="h-[70vh] flex items-center justify-center">
              <Loading size="lg" />
            </div>
          ) : shouldShowSourceError ? (
            <div className="h-[70vh] flex items-center justify-center">
              <ErrorState
                title="Error cargando workflow"
                description={sourceErrorMessage}
                size="sm"
              />
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 min-w-0">
                <WorkflowCanvas
                  definition={definitionDraft}
                  selectedStepId={selectedStepId}
                  onSelectStep={setSelectedStepId}
                  editable
                  onAddConnection={handleAddConnection}
                  onRemoveConnection={handleRemoveConnection}
                  onAddStep={handleRequestAddStep}
                  onDeleteStep={handleDeleteStep}
                  onAddFinalStep={
                    definitionDraft.finalStep
                      ? undefined
                      : handleRequestAddFinal
                  }
                  canAddFinalStep={!definitionDraft.finalStep}
                  reachableStepIds={reachableSteps}
                />
              </div>
              <WorkflowDetailsPanel
                definition={definitionDraft}
                selectedStepId={selectedStepId}
                editable
                inputSchema={inputSchemaDraft}
                onChangeInputSchema={setInputSchemaDraft}
                onChangeStepInputs={handleChangeStepInputs}
                onValidationChange={setInputValidationErrors}
                metadata={metadataDraft}
                onChangeMetadata={setMetadataDraft}
                onDeleteStep={handleDeleteStep}
              />
            </div>
          )}
        </div>
      </Panel>
      <WorkflowActionPickerModal
        isOpen={isAddModalOpen}
        onClose={closeActionPicker}
        pendingFinal={pendingFinal}
        actionSearch={actionSearch}
        onActionSearchChange={setActionSearch}
        actions={filteredActions}
        loading={loadingActions}
        onSelectAction={handleSelectAction}
      />
    </div>
  );
};

export default WorkflowCreatePage;
